import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ENGINE_TYPES, TRANSMISSIONS, STATUS_OPTIONS } from "@/lib/constants";
import { Search, Plus, Pencil, Trash2, Car } from "lucide-react";

const EMPTY = {
  customer_id: "",
  branch_id: "",
  brand: "",
  model: "",
  year: "",
  version: "",
  plates: "",
  vin: "",
  color: "",
  base_mileage: 0,
  engine_type: "gasoline",
  transmission_type: "automatic",
  notes: "",
  status: "active",
};

export default function Vehiculos() {
  const { isSuperAdmin, activeTenantId, branches } = useTenant();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  const customerMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  async function fetchAll() {
    if (!canQuery) {
      setItems([]);
      setCustomers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [{ data: v }, { data: c }] = await Promise.all([
        api.get("/vehicles", { params: { ...tenantParam, search: search || undefined } }),
        api.get("/customers", { params: tenantParam }),
      ]);
      setItems(v);
      setCustomers(c);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTenantId, isSuperAdmin]);

  function openCreate() {
    setEditing(null);
    const main = branches.find((b) => b.is_main) || branches[0];
    setForm({ ...EMPTY, branch_id: main?.id || "" });
    setSheetOpen(true);
  }
  function openEdit(v) {
    setEditing(v);
    setForm({ ...EMPTY, ...v, year: v.year || "", base_mileage: v.base_mileage || 0 });
    setSheetOpen(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        base_mileage: form.base_mileage ? Number(form.base_mileage) : 0,
        branch_id: form.branch_id || null,
      };
      if (editing) {
        await api.patch(`/vehicles/${editing.id}`, payload);
        toast.success("Vehículo actualizado");
      } else {
        await api.post("/vehicles", payload, { params: tenantParam });
        toast.success("Vehículo creado");
      }
      setSheetOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      await api.delete(`/vehicles/${confirmDelete.id}`);
      toast.success("Vehículo eliminado");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="page-vehiculos">
      <PageHeader
        title="Vehículos"
        description="Cada vehículo debe estar vinculado a un cliente y opcionalmente a una sucursal."
        actions={
          <Button
            className="btn-tenant"
            onClick={openCreate}
            disabled={!canQuery || customers.length === 0}
            data-testid="btn-new-vehiculo"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo vehículo
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por marca, modelo, placas o VIN…"
            className="pl-9 h-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-vehiculos"
          />
        </div>
        <div className="text-xs text-muted-foreground ml-auto" data-testid="vehiculos-count">
          {items.length} registrado{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? (customers.length === 0 ? "Primero registra un cliente" : "Aún no hay vehículos") : "Selecciona un tenant"}
            description={
              !canQuery
                ? "Como Super Admin VRIntegrales, primero elige un tenant desde Tenants."
                : customers.length === 0
                ? "Todo vehículo debe estar vinculado a un cliente. Registra al primero para empezar."
                : "Registra el primer vehículo del taller."
            }
            icon={Car}
            action={
              canQuery && customers.length > 0 && (
                <Button className="btn-tenant" onClick={openCreate} data-testid="empty-new-vehiculo">
                  <Plus className="h-4 w-4 mr-2" /> Crear vehículo
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Vehículo</TableHead>
                <TableHead>Placas / VIN</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead>Kilometraje</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((v) => (
                <TableRow key={v.id} className="tbl-row" data-testid={`row-vehiculo-${v.id}`}>
                  <TableCell>
                    <div className="font-medium">
                      {v.brand} {v.model}{" "}
                      <span className="text-muted-foreground font-normal">
                        {v.year || ""} {v.version || ""}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {v.color || "—"} · {ENGINE_TYPES.find((e) => e.value === v.engine_type)?.label} ·{" "}
                      {TRANSMISSIONS.find((t) => t.value === v.transmission_type)?.label}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="mono text-xs">{v.plates || "—"}</div>
                    <div className="mono text-[11px] text-muted-foreground">{v.vin || ""}</div>
                  </TableCell>
                  <TableCell className="text-sm">{customerMap[v.customer_id]?.legal_name || "—"}</TableCell>
                  <TableCell className="text-sm">{branchMap[v.branch_id]?.name || "—"}</TableCell>
                  <TableCell className="text-sm">{(v.base_mileage || 0).toLocaleString("es-MX")} km</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(v)} data-testid={`btn-edit-vehiculo-${v.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(v)} data-testid={`btn-delete-vehiculo-${v.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="sheet-vehiculo">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar vehículo" : "Nuevo vehículo"}</SheetTitle>
            <SheetDescription>Registra datos técnicos y de propiedad.</SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} required>
                <SelectTrigger data-testid="vehiculo-cliente"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marca *</Label>
                <Input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="vehiculo-brand" />
              </div>
              <div className="space-y-2">
                <Label>Modelo *</Label>
                <Input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="vehiculo-model" />
              </div>
              <div className="space-y-2">
                <Label>Año</Label>
                <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="vehiculo-year" />
              </div>
              <div className="space-y-2">
                <Label>Versión</Label>
                <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="vehiculo-version" />
              </div>
              <div className="space-y-2">
                <Label>Placas</Label>
                <Input value={form.plates} onChange={(e) => setForm({ ...form, plates: e.target.value.toUpperCase() })} data-testid="vehiculo-plates" />
              </div>
              <div className="space-y-2">
                <Label>VIN</Label>
                <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} data-testid="vehiculo-vin" />
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="vehiculo-color" />
              </div>
              <div className="space-y-2">
                <Label>Kilometraje base</Label>
                <Input type="number" value={form.base_mileage} onChange={(e) => setForm({ ...form, base_mileage: e.target.value })} data-testid="vehiculo-mileage" />
              </div>
              <div className="space-y-2">
                <Label>Motor</Label>
                <Select value={form.engine_type} onValueChange={(v) => setForm({ ...form, engine_type: v })}>
                  <SelectTrigger data-testid="vehiculo-engine"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENGINE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Transmisión</Label>
                <Select value={form.transmission_type} onValueChange={(v) => setForm({ ...form, transmission_type: v })}>
                  <SelectTrigger data-testid="vehiculo-transmission"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRANSMISSIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sucursal</Label>
                <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="vehiculo-branch"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estatus</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="vehiculo-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vehiculo-notes" />
            </div>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)} data-testid="vehiculo-cancel">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="btn-tenant" data-testid="vehiculo-save">
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear vehículo"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar vehículo</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <b>{confirmDelete?.brand} {confirmDelete?.model} · {confirmDelete?.plates}</b>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-vehiculo">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
